import type { AnimationEvent } from '@angular/animations';
import { Dir } from '@angular/cdk/bidi';
import type { OnChanges, SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Optional,
  Output,
  output,
} from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { RtTooltipInvalidPositionError } from './tooltip-errors';
import type { TooltipPosition, TooltipType, TooltipVisibility } from './tooltip-inner.component';
import { TooltipInnerComponent } from './tooltip-inner.component';

@Component({
  selector: 'rt-tooltip',
  template: `
    <rt-tooltip-inner
      [message]="message"
      [templateRef]="templateRef"
      [type]="type"
      [hasBorder]="hasBorder"
      [hasShadow]="hasShadow"
      [hasNoArrow]="hasNoArrow"
      [hasTextAlignCenter]="hasTextAlignCenter"
      [visibility]="visibility"
      [position]="position"
      [transformOrigin]="transformOrigin"
      [arrowOffset]="arrowOffset"
      [style.max-width.px]="maxWidth"
      [style.white-space]="wordWrap"
      [skipTransition]="skipTransition"
      (hide)="onTooltipHide()"
      (animationState)="animationState.next($event)"
    >
    </rt-tooltip-inner>
  `,
  host: {
    '(body:click)': 'this.handleBodyInteraction()',
  },
  imports: [TooltipInnerComponent],
})
export class TooltipComponent implements OnChanges {
  @Input() public message: string;
  @Input() public templateRef: TemplateRef<any> | null = null;
  @Input() public type: TooltipType = 'default';
  @Input() public hasBorder: boolean;
  @Input() public hasShadow: boolean;
  @Input() public hasNoArrow: boolean;
  @Input() public hasTextAlignCenter: boolean;
  @Input() public maxWidth: number = 300;
  @Input() public wordWrap: boolean | string;
  @Input() public skipTransition: boolean = false;
  @Input() public arrowOffset?: { offsetX: number; offsetY: number };

  /** Property watched by the animation framework to show or hide the tooltip */
  @Input() public visibility: TooltipVisibility = 'initial';

  @Output() public visibilityChange = new EventEmitter<TooltipVisibility>();

  /** Subject for notifying that the tooltip has been hidden from the view */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  public onHide = output<void>();

  public position: TooltipPosition;
  public whiteSpace: string;
  public animationState = new BehaviorSubject<AnimationEvent | null>(null);

  /** The transform origin used in the animation for showing and hiding the tooltip */
  public transformOrigin: string = 'above';

  constructor(
    @Optional() private dir: Dir,
    public cdRef: ChangeDetectorRef,
  ) {
    this.cdRef.detach();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('wordWrap' in changes) {
      this.whiteSpace =
        typeof this.wordWrap === 'boolean' ? (this.wordWrap ? 'normal' : 'nowrap') : this.wordWrap;
    }

    this.cdRef.detectChanges();
  }

  /**
   * Shows the tooltip with an animation originating from the provided origin
   * @param position Position of the tooltip.
   */
  public show(): void {
    this.visibility = 'visible';

    this.visibilityChange.emit(this.visibility);
    this.cdRef.detectChanges();
  }

  /**
   * Begins the animation to hide the tooltip after the provided delay in ms.
   */
  public hide(): void {
    this.visibility = 'hidden';
    this.visibilityChange.emit(this.visibility);
    this.cdRef.detectChanges();
  }

  public onTooltipHide(): void {
    this.onHide.emit();
  }

  /**
   * Interactions on the HTML body should close the tooltip immediately as defined in the
   * material design spec.
   * https://material.google.com/components/tooltips.html#tooltips-interaction
   */
  public handleBodyInteraction(): void {
    this.hide();
  }

  /** Sets the tooltip transform origin according to the tooltip position */
  public setTooltipPosition(value: TooltipPosition): void {
    const isLtr = !this.dir || this.dir.value === 'ltr';

    switch (value) {
      case 'before':
        this.transformOrigin = isLtr ? 'right' : 'left';
        break;
      case 'after':
        this.transformOrigin = isLtr ? 'left' : 'right';
        break;
      case 'left':
      case 'leftbelow':
        this.transformOrigin = 'right';
        break;
      case 'right':
      case 'aboveright':
      case 'righttop':
        this.transformOrigin = 'left';
        break;
      case 'above':
        this.transformOrigin = 'bottom';
        break;
      case 'below':
        this.transformOrigin = 'top';
        break;
      default:
        throw new RtTooltipInvalidPositionError(value);
    }

    this.position = value;
  }
}

export type TooltipInputs = Pick<
  TooltipComponent,
  | 'templateRef'
  | 'message'
  | 'type'
  | 'maxWidth'
  | 'wordWrap'
  | 'hasBorder'
  | 'hasShadow'
  | 'hasNoArrow'
  | 'hasTextAlignCenter'
  | 'skipTransition'
  | 'arrowOffset'
>;

export const TooltipComponentInputKeys: Array<keyof TooltipInputs> = [
  'type',
  'maxWidth',
  'wordWrap',
  'hasBorder',
  'hasShadow',
  'hasNoArrow',
  'message',
  'templateRef',
  'hasTextAlignCenter',
  'skipTransition',
  'arrowOffset',
];
